import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BsRobot, BsPerson, BsSend, BsLightningCharge } from 'react-icons/bs';
import { HiOutlineSparkles } from 'react-icons/hi2';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { getAIChatExplanation } from '../../api';
import { trackEvent } from '../../utils/analytics';

const DEMO_QUESTIONS = [
  {
    label: 'Trigonometry',
    question: 'If sin θ = 3/5, find cos θ and tan θ.',
    context: 'CBSE grade 10 Math',
  },
  {
    label: 'Newton\'s Laws',
    question: 'A 5 kg block is pushed with a force of 20 N on a frictionless surface. What is the acceleration?',
    context: 'CBSE grade 11 Physics',
  },
  {
    label: 'Organic Chemistry',
    question: 'Write the IUPAC name for CH3-CH(OH)-CH3.',
    context: 'CBSE grade 12 Chemistry',
  },
];

export default function AITutorDemo() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async (text, context) => {
    if (!text.trim()) return;

    const userMsg = { role: 'user', text: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setStarted(true);
    trackEvent("ai_demo_question_submitted", { label: context || "custom" });

    try {
      const systemPrefix = "You are a school exam tutor. ONLY answer questions related to school syllabus (CBSE, ICSE, IGCSE, IB, State Boards — Math, Science, Physics, Chemistry, Biology, Social Studies, English, Computer Science). If the question is NOT related to school syllabus, politely respond: 'I can only help with school syllabus questions. Please ask a question related to your school subjects.'\n\n";
      const prompt = context
        ? `${systemPrefix}Please explain the below question in the context of '${context}'. Keep the explanation clear, student-friendly, and exam-oriented.\n\nQuestion: ${text}`
        : `${systemPrefix}Question: ${text}`;

      const answer = await getAIChatExplanation(prompt);
      setMessages((prev) => [
        ...prev,
        { role: 'ai', text: answer || 'Sorry, I could not generate an explanation. Please try again.' },
      ]);
      trackEvent("ai_demo_response_received");
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'ai', text: 'An error occurred. Make sure the backend is running on port 5000.' },
      ]);
      trackEvent("ai_demo_response_failed", { reason: err?.message || "request_failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input, null);
  };

  const handleDemoQuestion = (dq) => {
    sendMessage(dq.question, dq.context);
  };

  return (
    <section className="section-padding relative overflow-hidden">
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-accent-600/10 rounded-full blur-[120px]" />

      <div className="container-custom mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-6">
            <HiOutlineSparkles className="text-accent-400" />
            <span className="text-sm font-medium text-gray-300">Try it Live</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold font-display text-white mb-4">
            Meet your <span className="gradient-text">AI Tutor</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Ask any question and get step-by-step explanations instantly.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <div className="glass-strong rounded-3xl overflow-hidden shadow-2xl shadow-black/30">
            {/* Chat Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center">
                <BsRobot className="text-white text-lg" />
              </div>
              <div>
                <p className="font-semibold text-white text-sm">ZenQB AI Tutor</p>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs text-gray-400">Online — ask anything</span>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-1.5 glass rounded-full px-3 py-1.5">
                <BsLightningCharge className="text-accent-400 text-xs" />
                <span className="text-xs text-gray-300">Powered by Llama 3</span>
              </div>
            </div>

            {/* Chat Messages */}
            <div ref={chatRef} className="p-6 space-y-5 max-h-[480px] overflow-y-auto min-h-[320px]">
              {!started && (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-6">Pick a question to try, or type your own:</p>
                  <div className="flex flex-wrap justify-center gap-3">
                    {DEMO_QUESTIONS.map((dq) => (
                      <button
                        key={dq.label}
                        onClick={() => handleDemoQuestion(dq)}
                        className="glass rounded-xl px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-all"
                      >
                        <span className="font-medium">{dq.label}</span>
                        <span className="text-gray-500 ml-2 text-xs">{dq.context}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'ai' && (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <BsRobot className="text-white text-sm" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-5 py-3.5 ${
                      msg.role === 'user'
                        ? 'bg-brand-600 text-white'
                        : 'glass text-gray-200'
                    }`}
                  >
                    {msg.role === 'ai' ? (
                      <div className="text-sm leading-relaxed ai-markdown">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkMath]}
                          rehypePlugins={[rehypeKatex]}
                          components={{
                            h1: ({children}) => <h1 className="text-xl font-bold font-display text-white mt-6 mb-3">{children}</h1>,
                            h2: ({children}) => <h2 className="text-lg font-bold font-display text-white mt-5 mb-2">{children}</h2>,
                            h3: ({children}) => <h3 className="text-base font-bold font-display text-white mt-4 mb-2">{children}</h3>,
                            p: ({children}) => <p className="mb-3 text-gray-200 leading-relaxed">{children}</p>,
                            ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-200">{children}</ul>,
                            ol: ({children}) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-200">{children}</ol>,
                            li: ({children}) => <li className="leading-relaxed">{children}</li>,
                            strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                            em: ({children}) => <em className="text-gray-300">{children}</em>,
                            blockquote: ({children}) => (
                              <blockquote className="border-l-4 border-brand-400 pl-4 py-2 my-3 bg-brand-500/10 rounded-r-lg text-gray-300">
                                {children}
                              </blockquote>
                            ),
                            code: ({inline, className, children, ...props}) => {
                              if (inline) {
                                return <code className="bg-white/10 text-accent-300 px-1.5 py-0.5 rounded text-xs font-mono" {...props}>{children}</code>;
                              }
                              return (
                                <div className="my-3 rounded-xl overflow-hidden border border-white/10">
                                  <div className="flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/10">
                                    <span className="text-xs text-gray-400 font-mono">code</span>
                                  </div>
                                  <pre className="p-4 overflow-x-auto bg-black/30">
                                    <code className="text-xs font-mono text-gray-200 leading-relaxed" {...props}>{children}</code>
                                  </pre>
                                </div>
                              );
                            },
                            table: ({children}) => (
                              <div className="my-4 overflow-x-auto rounded-xl border border-white/10">
                                <table className="w-full text-sm text-left">{children}</table>
                              </div>
                            ),
                            thead: ({children}) => <thead className="bg-white/5 border-b border-white/10">{children}</thead>,
                            tbody: ({children}) => <tbody className="divide-y divide-white/5">{children}</tbody>,
                            th: ({children}) => <th className="px-4 py-2.5 font-semibold text-white text-xs uppercase tracking-wider">{children}</th>,
                            td: ({children}) => <td className="px-4 py-2.5 text-gray-300">{children}</td>,
                            hr: () => <hr className="my-4 border-white/10" />,
                            a: ({href, children}) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300 underline">{children}</a>,
                          }}
                        >
                          {msg.text}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="text-sm leading-relaxed">{msg.text}</div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center flex-shrink-0 mt-1">
                      <BsPerson className="text-white text-sm" />
                    </div>
                  )}
                </motion.div>
              ))}

              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                    <BsRobot className="text-white text-sm" />
                  </div>
                  <div className="glass rounded-2xl px-5 py-3.5">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Input Bar */}
            <div className="px-6 py-4 border-t border-white/10">
              <form onSubmit={handleSubmit} className="flex items-center gap-3 glass rounded-2xl px-5 py-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask any question..."
                  disabled={loading}
                  className="flex-1 bg-transparent text-sm text-gray-200 outline-none placeholder-gray-500 disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="w-9 h-9 rounded-xl bg-gradient-to-r from-brand-500 to-accent-500 flex items-center justify-center hover:scale-110 transition-transform disabled:opacity-40 disabled:hover:scale-100"
                >
                  <BsSend className="text-white text-xs" />
                </button>
              </form>
              <div className="flex items-center justify-between mt-3 px-1">
                <p className="text-xs text-gray-600">
                  Backend must be running on port 5000
                </p>
                <Link to="/mode?category=school" onClick={() => trackEvent("solve_clicked", { source: "landing_ai_demo" })} className="text-xs text-brand-400 hover:text-brand-300 transition-colors">
                  Open full app →
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
